import type { TemplateField } from '../../lib/templates/types'

interface TemplateFormFieldProps {
  field: TemplateField
  value: any
  onChange: (key: string, value: any) => void
  error?: string
}

export function TemplateFormField({
  field,
  value,
  onChange,
  error,
}: TemplateFormFieldProps) {
  const handleChange = (newValue: any) => {
    onChange(field.key, newValue)
  }

  const inputBaseClass =
    'w-full px-3 py-2 rounded-md border border-border-subtle bg-bg-surface-hover text-text-primary placeholder-text-tertiary transition-colors focus:outline-none focus:ring-2 focus:ring-primary-red/50'

  const labelClass = 'text-caption font-semibold text-text-secondary uppercase tracking-wide'

  return (
    <div className="flex flex-col gap-2">
      <label className={labelClass}>
        {field.label}
        {field.required && <span className="text-primary-red ml-1">*</span>}
      </label>

      {field.type === 'text' && (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          className={inputBaseClass}
        />
      )}

      {field.type === 'textarea' && (
        <textarea
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          className={`${inputBaseClass} resize-none min-h-[100px]`}
        />
      )}

      {field.type === 'rich_text' && (
        <textarea
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          className={`${inputBaseClass} resize-none min-h-[120px]`}
        />
      )}

      {field.type === 'number' && (
        <input
          type="number"
          value={value === null || value === undefined ? '' : value}
          onChange={(e) => handleChange(e.target.value === '' ? null : parseFloat(e.target.value))}
          placeholder={field.placeholder}
          className={inputBaseClass}
          min={field.validation?.min}
          max={field.validation?.max}
        />
      )}

      {field.type === 'url' && (
        <input
          type="url"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          className={inputBaseClass}
        />
      )}

      {field.type === 'date' && (
        <input
          type="date"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value || null)}
          className={inputBaseClass}
        />
      )}

      {field.type === 'enum' && field.enumValues && (
        <select
          value={value || ''}
          onChange={(e) => handleChange(e.target.value || null)}
          className={inputBaseClass}
        >
          <option value="">Select an option...</option>
          {field.enumValues.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      {field.type === 'multi_enum' && field.enumValues && (
        <div className="flex flex-col gap-2">
          {field.enumValues.map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={Array.isArray(value) && value.includes(option.value)}
                onChange={(e) => {
                  const current = Array.isArray(value) ? [...value] : []
                  if (e.target.checked) {
                    if (!current.includes(option.value)) {
                      current.push(option.value)
                    }
                  } else {
                    const idx = current.indexOf(option.value)
                    if (idx > -1) {
                      current.splice(idx, 1)
                    }
                  }
                  handleChange(current)
                }}
                className="w-4 h-4 rounded border-border-subtle bg-bg-surface-hover cursor-pointer accent-primary-red"
              />
              <span className="text-body text-text-primary">{option.label}</span>
            </label>
          ))}
        </div>
      )}

      {field.type === 'boolean' && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => handleChange(e.target.checked)}
            className="w-4 h-4 rounded border-border-subtle bg-bg-surface-hover cursor-pointer accent-primary-red"
          />
          <span className="text-body text-text-primary">
            {field.description || 'Enabled'}
          </span>
        </label>
      )}

      {field.type === 'id_reference' && (
        <div className="flex flex-col gap-1">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            className={inputBaseClass}
          />
          {field.description && (
            <p className="text-caption text-text-tertiary">{field.description}</p>
          )}
        </div>
      )}

      {error && (
        <p className="text-caption text-primary-red">{error}</p>
      )}

      {field.description && field.type !== 'id_reference' && (
        <p className="text-caption text-text-tertiary">{field.description}</p>
      )}
    </div>
  )
}
